import { useEffect, useMemo, useState, type ReactNode } from "react";
import { getSupabaseConfigInfo } from "@/ui/data/supabaseConfig";

export default function EnvGuard({ children }: { children: ReactNode }) {
  const config = useMemo(() => getSupabaseConfigInfo(), []);
  const [runtimeHealth, setRuntimeHealth] = useState<{
    status: "idle" | "checking" | "ok" | "error";
    message: string | null;
  }>({
    status: "idle",
    message: null,
  });

  useEffect(() => {
    if (config.errors.length > 0) return;
    if (!config.isFunctionsHostLocal || !config.functionsHealthCheckUrl) return;

    let cancelled = false;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 4500);
    setRuntimeHealth({ status: "checking", message: null });

    void fetch(config.functionsHealthCheckUrl, {
      method: "GET",
      signal: controller.signal,
    }).then((response) => {
      if (cancelled) return;
      if (!response.ok) {
        setRuntimeHealth({
          status: "error",
          message: `Local Mythic API health check failed (${response.status}) at ${config.functionsHealthCheckUrl}.`,
        });
        return;
      }
      setRuntimeHealth({ status: "ok", message: null });
    }).catch(() => {
      if (cancelled) return;
      setRuntimeHealth({
        status: "error",
        message: `Local Mythic API is unreachable at ${config.functionsHealthCheckUrl}. Start services/mythic-api or switch VITE_MYTHIC_FUNCTIONS_BASE_URL.`,
      });
    }).finally(() => {
      window.clearTimeout(timeout);
    });

    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, [
    config.errors.length,
    config.functionsHealthCheckUrl,
    config.isFunctionsHostLocal,
  ]);

  if (config.errors.length > 0) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="max-w-md rounded-lg border border-border bg-card p-6 text-sm">
          <div className="mb-2 text-lg font-semibold text-foreground">Supabase configuration error</div>
          <p className="text-muted-foreground">
            Fix the following configuration issues before running the app:
          </p>
          <ul className="mt-3 list-disc space-y-1 pl-5 text-muted-foreground">
            {config.errors.map(error => (
              <li key={error}>{error}</li>
            ))}
          </ul>
          <div className="mt-4 rounded-md border border-border bg-background p-3 text-xs text-muted-foreground">
            <div>Host: {config.host ?? "-"}</div>
            <div>Client key type: {config.keyType}</div>
            <div>Client key source: {config.keySource ?? "-"}</div>
            <div>Client key length: {config.keyLength}</div>
            <div>Client key: {config.maskedKey ?? "-"}</div>
            <div>Functions base: {config.functionsBaseUrl ?? "-"}</div>
            <div>Functions host: {config.functionsBaseHost ?? "-"}</div>
            <div>Functions source: {config.functionsBaseSource ?? "-"}</div>
            <div>Runtime provider: {config.runtimeProvider}</div>
            <div>Functions health URL: {config.functionsHealthCheckUrl ?? "-"}</div>
            {config.warnings.length > 0 ? (
              <div className="mt-2">
                <div className="font-medium text-foreground/80">Warnings</div>
                <ul className="mt-1 list-disc space-y-1 pl-5">
                  {config.warnings.map(w => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  if (runtimeHealth.status === "error") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="max-w-md rounded-lg border border-border bg-card p-6 text-sm">
          <div className="mb-2 text-lg font-semibold text-foreground">Functions runtime unreachable</div>
          <p className="text-muted-foreground">
            {runtimeHealth.message}
          </p>
          <div className="mt-4 rounded-md border border-border bg-background p-3 text-xs text-muted-foreground">
            <div>Functions base: {config.functionsBaseUrl ?? "-"}</div>
            <div>Runtime provider: {config.runtimeProvider}</div>
            <div>Health URL: {config.functionsHealthCheckUrl ?? "-"}</div>
            <div>Mode: localhost safety check</div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
